import _ from "lodash"
import moment from "moment"
import path from "path"
import {generateFetchFieldListAction} from "../../../../apps/KpModule/actions/api"
import {setFieldVisibility, changeFieldDisabled} from "../../../../apps/KpModule/actions"
import {decrypt} from "../../../utils/crypto"
import Errors from "../../../utils/Errors"
import {generateAuditReportExcel} from "./generateAuditReport"

const saveButton        = { type: 'save', tKey: 'save', bsStyle: 'success' }
const prepareButton     = { type: 'action', action: 'prepare', tKey: 'prepare', bsStyle: 'primary' }
const realizeButton     = { type: 'action', action: 'realize', tKey: 'realize', bsStyle: 'primary' }
const communicateButton = (confirmationMessage) => ({
    type: 'action',
    action: 'communicate',
    tKey: 'communicate',
    bsStyle: 'primary',
    getUserConfirmation: true,
    confirmationMessage: confirmationMessage
})
const closeButton       = { type: 'action', action: 'close', tKey: 'close', bsStyle: 'danger' }
const relanceButton     = { type: 'action', action: 'relance', tKey: 'relance', bsStyle: 'warning' }
const returnButton      = { type: 'return', tKey: 'return', bsStyle: 'default' }


export const entity = {
    name: "AuditPreparation",
    facets: [{name: "files", linkType: "OTO"}],
    fields: [
        {path: "auditNumber"},
        {path: "store", type: "Store", unique: true, uniqueWith: ["controlDate"]},
        {path: "auditors", type: "Habilitation", link: "MTM"},
        {path: "storeContacts", type: "Habilitation", link: "MTM", nullable: true},
        {path: "hierarchicalContacts", type: "Habilitation", link: "MTM", nullable: true},
        {path: "status", type: "AuditPreparationStatus", default: {id: 'draft'}, nullable: true},
        {path: "auditThemes", type: "AuditTheme", link: "MTM"},
        {path: "objectives"},
        {path: "controlDate"},
        {path: "relanceCount", type: "integer", nullable: true},
        {path: "initialCount", type: "integer", nullable: true},
        {
            path: "realizedCount",
            type: "integer",
            $f: async function(auditPreparation, context, callback) {
                try {
                    const controls = await global.app.S.AuditPreparationControl.find({
                        query: {auditPreparation: new global.ObjectID(auditPreparation.id)},
                        group: context.group,
                        fieldPath: ['status.id']
                    })
                    const count = controls.filter(c => ['closed', 'inFollowUp', 'pendingDecision'].includes(_.get(c, 'status.id'))).length
                    return callback(null, count)
                } catch(e) { return callback(e) }
            }
        },
        {
            path: "followUpCount",
            type: "integer",
            $f: async function(auditPreparation, context, callback) {
                try {
                    const controls = await global.app.S.AuditPreparationControl.find({
                        query: {auditPreparation: new global.ObjectID(auditPreparation.id)},
                        group: context.group,
                        fieldPath: ['status.id']
                    })
                    const count = controls.filter(c => _.get(c, 'status.id') === 'inFollowUp').length
                    return callback(null, count)
                } catch(e) { return callback(e) }
            }
        },
        {
            path: "closedCount",
            type: "integer",
            $f: async function(auditPreparation, context, callback) {
                try {
                    const controls = await global.app.S.AuditPreparationControl.find({
                        query: {auditPreparation: new global.ObjectID(auditPreparation.id)},
                        group: context.group,
                        fieldPath: ['status.id']
                    })
                    const count = controls.filter(c => _.get(c, 'status.id') === 'closed').length
                    return callback(null, count)
                } catch(e) { return callback(e) }
            }
        },
        {
            path: "notApplicableCount",
            type: "integer",
            $f: async function(auditPreparation, context, callback) {
                try {
                    const controls = await global.app.S.AuditPreparationControl.find({
                        query: {auditPreparation: new global.ObjectID(auditPreparation.id)},
                        group: context.group,
                        fieldPath: ['status.id']
                    })
                    const count = controls.filter(c => _.get(c, 'status.id') === 'notApplicable').length
                    return callback(null, count)
                } catch(e) { return callback(e) }
            }
        },
        {
            path: "pendingDecisionCount",
            type: "integer",
            $f: async function(auditPreparation, context, callback) {
                try {
                    const controls = await global.app.S.AuditPreparationControl.find({
                        query: {auditPreparation: new global.ObjectID(auditPreparation.id)},
                        group: context.group,
                        fieldPath: ['status.id']
                    })
                    const count = controls.filter(c => _.get(c, 'status.id') === 'pendingDecision').length
                    return callback(null, count)
                } catch(e) { return callback(e) }
            }
        },
        {
            path: "auditScore",
            type: "integer",
            nullable: true
        },
        {path: "updatedAt", type: "date", dateFormat: 'YYYY-MM-DD HH:mm', nullable: true},
        {path: "updatedBy", type: "User", nullable: true},
        {
            path: "orangeStyledRow",
            fieldPath: ['id'],
            $f: async function(auditPreparation, context, callback) {
                try {
                    const today = moment().startOf('day').toDate()
                    const controls = await global.app.S.AuditPreparationControl.find({
                        query: {
                            auditPreparation: new global.ObjectID(auditPreparation.id),
                            status: 'inFollowUp',
                            implementationDate: {$lt: today}
                        },
                        group: context.group,
                        fieldPath: ['id']
                    })
                    return callback(null, controls.length > 0)
                } catch(e) { return callback(e) }
            }
        },
        {
            path: "buttons",
            fieldPath: ['status.id'],
            $f: async function(auditPreparation, context, callback) {
                try {
                    const statusId = _.get(auditPreparation, 'status.id')
                    const buttons = []

                    const isDraft         = !statusId || statusId === 'draft'
                    const isInPreparation = statusId === 'inPreparation'
                    const isInRealization = statusId === 'inRealization'

                    const today = moment().startOf('day').toDate()
                    const controls = await global.app.S.AuditPreparationControl.find({
                        query: {auditPreparation: new global.ObjectID(auditPreparation.id)},
                        group: context.group,
                        fieldPath: ['status.id', 'implementationDate']
                    })
                    const closableStatuses = ['closed', 'notApplicable', 'notControlled']
                    const allControlsAreClosed = controls.length > 0 && controls.every(c => closableStatuses.includes(_.get(c, 'status.id')))
                    const hasOverdueFollowUp = controls.some(c =>
                        _.get(c, 'status.id') === 'inFollowUp' &&
                        c.implementationDate && new Date(c.implementationDate) < today
                    )
                    const inRealizationCount = controls.filter(c => _.get(c, 'status.id') === 'inRealization').length
                    const confirmationMessage = inRealizationCount > 0
                        ? context.tc('communicateConfirmationMessageWithCount', { count: inRealizationCount })
                        : context.tc('communicateConfirmationMessage')

                    if (statusId !== 'closed') buttons.push(saveButton)
                    if (isDraft) buttons.push(prepareButton)
                    if (isInPreparation) buttons.push(realizeButton)
                    if (isInRealization) buttons.push(communicateButton(confirmationMessage))
                    if (allControlsAreClosed && statusId !== 'closed') buttons.push(closeButton)
                    if (hasOverdueFollowUp) buttons.push(relanceButton)

                    buttons.push(returnButton)
                    return callback(null, buttons)
                } catch(e) {
                    return callback(e)
                }
            }
        }
    ],
    filters: [
        {
            name: "byStatuses",
            path: 'statuses',
            sortList: false,
            object: 'AuditPreparationStatus',
            display: "name",
            translate: true,
            client: true,
            default: [{id: 'draft'}, {id: 'inPreparation'}, {id: 'inRealization'}],
            isDefault: false,
            type: 'tags',
            width: 12,
            query: function(context) {
                const statuses = _.get(context, 'data.statuses') || []
                if (!statuses.length) return {}
                return {status: {$in: statuses.map(s => s.id)}}
            }
        },
        {
            name: "excludeDraft",
            isDefault: false,
            query: () => ({status: {$ne: 'draft'}})
        },
        {
            name: "byCurrentUser",
            isDefault: false,
            async: true,
            query: async function(context, callback) {
                try {
                    const userId = context.user.id
                    const preps = await global.app.S.AuditPreparation.find({
                        group: context.group,
                        query: {},
                        fieldPath: ['id', 'auditors.user.id', 'storeContacts.user.id', 'hierarchicalContacts.user.id']
                    })
                    const auditorPrepIds = []
                    const contactPrepIds = []
                    preps.forEach(p => {
                        const isAuditor = (_.get(p, 'auditors') || []).some(a => _.get(a, 'user.id') === userId)
                        const isSC = (_.get(p, 'storeContacts') || []).some(a => _.get(a, 'user.id') === userId)
                        const isHC = (_.get(p, 'hierarchicalContacts') || []).some(a => _.get(a, 'user.id') === userId)
                        if (isAuditor) auditorPrepIds.push(p.id)
                        else if (isSC || isHC) contactPrepIds.push(p.id)
                    })
                    let filteredContactPrepIds = []
                    if (contactPrepIds.length) {
                        const controls = await global.app.S.AuditPreparationControl.find({
                            group: context.group,
                            query: {
                                auditPreparation: {$in: contactPrepIds.map(id => new global.ObjectID(id))},
                                status: 'inFollowUp'
                            },
                            fieldPath: ['auditPreparation.id']
                        })
                        const prepIdsWithFollowUp = new Set(controls.map(c => _.get(c, 'auditPreparation.id')))
                        filteredContactPrepIds = contactPrepIds.filter(id => prepIdsWithFollowUp.has(id))
                    }
                    const ids = [...auditorPrepIds, ...filteredContactPrepIds].map(id => new global.ObjectID(id))
                    if (!ids.length) return callback(null, {_id: null})
                    return callback(null, {_id: {$in: ids}})
                } catch(e) {
                    return callback(null, {_id: null})
                }
            }
        }
    ],
    validateSave: function(object, oldObject, context, callback) {
        const hcIds = (object.hierarchicalContacts || []).map(hc => hc.id)
        const auditorIds = (object.auditors || []).map(a => a.id)
        if (hcIds.some(hc => auditorIds.includes(hc)) && auditorIds.some(a => hcIds.includes(a))) {
            return callback(new Errors.ValidationError('hierarchicalContactCannotBeAuditor'))
        }
        return callback()
    },
    beforeSave: async (object, oldObject, context, callback) => {
        try {
            object.updatedAt = new Date()
            object.updatedBy = _.pick(context.user, ['id', 'name'])

            const action = context.action
            const isCreate = context.restAction && context.restAction.crudType === 'C'

            // ── Enregistrer ──────────────────────────────────────────────
            if (!action || action === 'save') {
                if (isCreate) object.status = {id: 'draft'}
                return callback(null, object, oldObject)
            }

            // ── Préparer ─────────────────────────────────────────────────
            if (action === 'prepare') {
                object.status = {id: 'inPreparation'}
                object.auditNumber = `${object.store.code} - ${object.controlDate}`

                const themeIds = (object.auditThemes || []).map(t => new global.ObjectID(t.id))
                const activeControls = await global.app.S.AuditControl.find({
                    query: {auditTheme: {$in: themeIds}, active: true},
                    group: context.group,
                    fieldPath: ['id']
                })

                const inPreparationStatusId = 'inPreparation'
                if (activeControls.length > 0) {
                    await global.app.S.AuditPreparationControl.collection.insertMany(
                        activeControls.map(ctrl => ({
                            auditPreparation: new global.ObjectID(object.id),
                            auditControl: new global.ObjectID(ctrl.id),
                            status: inPreparationStatusId,
                            conversations: [{
                                sender: _.pick(context.user, ['id', 'name']),
                                timestamp: new Date().toISOString(),
                                seenBy: [context.user.id],
                                files: [],
                                from: 'system',
                                text: inPreparationStatusId,
                            }],
                            group: new global.ObjectID(context.group.id),
                        }))
                    )
                }

                object.initialCount = activeControls.length
                return callback(null, object, oldObject)
            }

            // ── Réaliser ──────────────────────────────────────────────────
            if (action === 'realize') {
                object.status = {id: 'inRealization'}

                await global.app.S.AuditPreparationControl.collection.updateMany(
                    {
                        auditPreparation: new global.ObjectID(object.id),
                        status: 'inPreparation'
                    },
                    {
                        $set: {status: 'inRealization'},
                        $push: {conversations: {
                            sender: _.pick(context.user, ['id', 'name']),
                            timestamp: new Date().toISOString(),
                            seenBy: [context.user.id],
                            files: [],
                            from: 'system',
                            text: 'inRealization'
                        }}
                    }
                )

                return callback(null, object, oldObject)
            }

            // ── Communiquer ───────────────────────────────────────────────
            if (action === 'communicate') {
                const controls = await global.app.S.AuditPreparationControl.find({
                    query: {
                        auditPreparation: new global.ObjectID(object.id),
                        status: 'inRealization'
                    },
                    group: context.group,
                    fieldPath: ['id', 'conformityRate.id', 'auditorRecommendation']
                })

                const withoutRate = controls.filter(c => !_.get(c, 'conformityRate.id'))
                const withRate    = controls.filter(c => !!_.get(c, 'conformityRate.id'))

                const makeConversationEntry = (text, from = 'system') => ({
                    sender: _.pick(context.user, ['id', 'name']),
                    timestamp: new Date().toISOString(),
                    seenBy: [context.user.id],
                    files: [],
                    from,
                    text
                })

                // Controls without conformityRate → notControlled
                if (withoutRate.length > 0) {
                    await global.app.S.AuditPreparationControl.collection.updateMany(
                        {_id: {$in: withoutRate.map(c => new global.ObjectID(c.id))}},
                        {
                            $set: {status: 'notControlled'},
                            $push: {conversations: makeConversationEntry('notControlled')}
                        }
                    )
                }

                for (const ctrl of withRate) {
                    const isClosed = _.get(ctrl, 'conformityRate.id') === '100'
                    const newStatus = isClosed ? 'closed' : 'inFollowUp'
                    const recommendation = ctrl.auditorRecommendation || ''

                    const $pushEntries = []
                    if (recommendation) $pushEntries.push(makeConversationEntry(recommendation, 'user'))

                    $pushEntries.push(makeConversationEntry(newStatus))

                    await global.app.S.AuditPreparationControl.collection.updateOne(
                        {_id: new global.ObjectID(ctrl.id)},
                        {
                            $set: {status: newStatus},
                            $push: {conversations: {$each: $pushEntries}}
                        }
                    )
                }

                const allAuditControls = await global.app.S.AuditPreparationControl.find({
                    query: {auditPreparation: new global.ObjectID(object.id)},
                    group: context.group,
                    fieldPath: [
                        'id', 'status.id', 'conformityRate.id', 'auditControl.weight.value',
                        'auditControl.code', 'auditControl.name',
                        'auditControl.auditTheme.id', 'auditControl.auditTheme.code', 'auditControl.auditTheme.name',
                        'initialAuditorComment', 'auditorRecommendation', 'implementationDate'
                    ]
                })
                const stillHasFollowUp = allAuditControls.some(c => !['notControlled', 'notApplicable', 'closed'].includes(_.get(c, 'status.id')))
                object.status = {id: stillHasFollowUp ? 'inFollowUp' : 'closed'}

                const controlsForScore = allAuditControls.filter(c => !['notApplicable', 'notControlled'].includes(_.get(c, 'status.id')))
                const denominator = controlsForScore.reduce((sum, c) => sum + (_.get(c, 'auditControl.weight.value') || 0), 0)
                const numerator   = controlsForScore.reduce((sum, c) => {
                    const weight = _.get(c, 'auditControl.weight.value') || 0
                    const rate   = parseInt(_.get(c, 'conformityRate.id') || '0', 10)
                    return sum + weight * rate
                }, 0)
                object.auditScore = denominator > 0 ? numerator / denominator : null

                // Fetch contact habilitations for report and email
                const scIds = (object.storeContacts || []).map(sc => sc.id).filter(Boolean)
                const hcIds = (object.hierarchicalContacts || []).map(hc => hc.id).filter(Boolean)
                const allContactIds = [...new Set([...scIds, ...hcIds])]
                const habs = allContactIds.length > 0
                    ? await global.app.S.Habilitation.find({
                        query: {_id: {$in: allContactIds.map(id => new global.ObjectID(id))}},
                        group: context.group,
                        fieldPath: ['id', 'user.id', 'user.mail', 'user.name', 'user.language.id']
                    })
                    : []
                const habsById = _.keyBy(habs, 'id')

                // Generate audit report xlsx
                const reportFilename = `${object.auditNumber} - ${moment().format('YYYY-MM-DD')}.xlsx`
                const reportFile = await generateAuditReportExcel(
                    {
                        auditPreparation: {
                            auditNumber: object.auditNumber,
                            controlDate: object.controlDate,
                            auditScore: object.auditScore,
                            store: object.store,
                            storeContacts: scIds.map(id => ({ userName: _.get(habsById[id], 'user.name') || '' })),
                            hierarchicalContacts: hcIds.map(id => ({ userName: _.get(habsById[id], 'user.name') || '' }))
                        },
                        controls: allAuditControls,
                        language: context.user?.language?.id,
                        context
                    },
                    reportFilename
                )
                object.file = {
                    ...reportFile,
                    id: new global.ObjectID(reportFile.id),
                    user: _.pick(_.get(context, "user"), ['id', 'name']),
                    date: moment().format('YYYY-MM-DD HH:mm')
                }

                // Send email to storeContacts and hierarchicalContacts
                const recipients = habs.map(h => _.get(h, 'user.mail') ? {mail: decrypt(h.user.mail), language: _.get(h, 'user.language.id') || 'fr'} : null).filter(Boolean)
                if (recipients.length > 0 && stillHasFollowUp) {
                    const mailContext = {
                        auditNumber: object.auditNumber,
                        realizedCount: object.realizedCount,
                        followUpCount: object.followUpCount,
                        link: global.isProd
                            ? `https://${global.config.host}/business/${context.group.id}`
                            : `http://localhost:3000/business/${context.group.id}`
                    }
                    const senderMail = context.user.mail
                    const senderDisplay = context.user.name
                        ? `"${context.user.name}" <${senderMail}>`
                        : senderMail
                    const communicateSubjects = {
                        fr: `Audit ${object.auditNumber} : Actions attendues à l'issue de l'audit`,
                        en: `Audit ${object.auditNumber} : Actions expected following the audit`,
                        it: `Audit ${object.auditNumber} : Azioni attese a seguito dell'audit`
                    }
                    const mails = recipients.map(({mail: to, language}) => {
                        const lang = ['fr', 'en', 'it'].includes(language) ? language : 'fr'
                        return {
                            from: '"Keenpoint" <pilotflow@keenpoint.com>',
                            to,
                            cc: senderMail ? senderDisplay : undefined,
                            replyTo: senderMail ? senderDisplay : undefined,
                            subject: {template: communicateSubjects[lang]},
                            content: `${lang}_auditCommunicate.html`,
                            templateDir: path.join(
                                global.appRoot,
                                global.isProd ? 'buildServer' : 'src',
                                '/server/models/slp/audit/templates'
                            ),
                            context: mailContext,
                            verbose: {general: true},
                            signature: false
                        }
                    })

                    await new Promise(resolve => {
                        global.mailer.sendMail(mails, error => {
                            if (error) console.error('Audit communicate email error:', error)
                            resolve()
                        })
                    })
                }

                return callback(null, object, oldObject)
            }

            // ── Relancer ──────────────────────────────────────────────────
            if (action === 'relance') {
                object.relanceCount = (object.relanceCount || 0) + 1
                const contactIds = [
                    ...(object.storeContacts || []).map(sc => sc.id),
                    ...(object.hierarchicalContacts || []).map(hc => hc.id),
                ].filter(Boolean)
                if (contactIds.length > 0) {
                    const habs = await global.app.S.Habilitation.find({
                        query: {_id: {$in: contactIds.map(id => new global.ObjectID(id))}},
                        group: context.group,
                        fieldPath: ['id', 'user.id', 'user.mail', 'user.language.id']
                    })
                    const recipients = habs.map(h => _.get(h, 'user.mail') ? {mail: decrypt(h.user.mail), language: _.get(h, 'user.language.id') || 'fr'} : null).filter(Boolean)
                    if (recipients.length > 0) {
                        const mailContext = {
                            auditNumber: object.auditNumber,
                            link: global.isProd
                                ? `https://${global.config.host}/business/${context.group.id}`
                                : `http://localhost:3000/business/${context.group.id}`
                        }
                        const senderMail = context.user.mail
                        const senderDisplay = context.user.name
                            ? `"${context.user.name}" <${senderMail}>`
                            : senderMail
                        const relanceSubjects = {
                            fr: `Audit ${object.auditNumber} : Suivi des actions issues de l'audit`,
                            en: `Audit ${object.auditNumber} : Follow-up of actions arising from the audit`,
                            it: `Audit ${object.auditNumber} : Monitoraggio delle azioni risultanti dall'audit`
                        }
                        const mails = recipients.map(({mail: to, language}) => {
                            const lang = ['fr', 'en', 'it'].includes(language) ? language : 'fr'
                            return {
                                from: '"Keenpoint" <pilotflow@keenpoint.com>',
                                to,
                                cc: senderMail ? senderDisplay : undefined,
                                replyTo: senderMail ? senderDisplay : undefined,
                                subject: {template: relanceSubjects[lang]},
                                content: `${lang}_auditRelance.html`,
                                templateDir: path.join(
                                    global.appRoot,
                                    global.isProd ? 'buildServer' : 'src',
                                    '/server/models/slp/audit/templates'
                                ),
                                context: mailContext,
                                verbose: {general: true},
                                signature: false
                            }
                        })

                        await new Promise(resolve => {
                            global.mailer.sendMail(mails, error => {
                                if (error) console.error('Audit relance email error:', error)
                                resolve()
                            })
                        })
                    }
                }
                return callback(null, object, oldObject)
            }

            // ── Clôturer ──────────────────────────────────────────────────
            if (action === 'close') {
                object.status = {id: 'closed'}
                return callback(null, object, oldObject)
            }

            return callback(null, object, oldObject)
        } catch (e) {
            return callback(e)
        }
    }
}

export const module_ = {
    object: "AuditPreparation",
    tKey: "mTitle_auditPreparation",
    category: {
        path: "audit",
        icon: "clipboardCheck"
    },
    defaultSortBy: "store",
    defaultSortDirection: "ASC",
    defaultFormButtons: [saveButton, prepareButton, returnButton],
    filters: ["byStatuses"],
    viewMap: {
        dt: [
            {path: "auditNumber", tKey: "auditNumber", initiallyNotVisible: true},
            {path: "store", display: "fullName", tKey: "store"},
            {path: "controlDate", tKey: "scheduledAuditDate"},
            {path: "storeContacts", display: "user.name", tKey: "storeContacts"},
            {path: "hierarchicalContacts", display: "user.name", tKey: "hierarchicalContacts"},
            {path: "auditors", display: "user.name", tKey: "auditors", initiallyNotVisible: true},
            {path: "status", display: "name", tKey: "status", translate: true},
            {path: "initialCount", tKey: "initialCount", initiallyNotVisible: true},
            {path: "realizedCount", tKey: "realizedCount", initiallyNotVisible: true},
            {path: "notApplicableCount", tKey: "notApplicableCount", initiallyNotVisible: true},
            {path: "followUpCount", tKey: "followUpCount", initiallyNotVisible: true},
            {path: "pendingDecisionCount", tKey: "pendingDecisionCount", initiallyNotVisible: true},
            {path: "closedCount", tKey: "closedCount", initiallyNotVisible: true},
            {path: "auditScore", tKey: "auditScore"},
            {path: "relanceCount", initiallyNotVisible: true},
            {path: "file", tKey: "report"},
            {path: "updatedBy", display: "name", tKey: "MAJ par", initiallyNotVisible: true},
            {path: "updatedAt", tKey: "MAJ le", initiallyNotVisible: true},
            {path: 'orangeStyledRow', hidden: true}
        ],
        form: {
            fields: [
                {path: "auditNumber", tKey: "auditNumber", disabled: true},
                {
                    path: "store",
                    display: "fullName",
                    tKey: "store",
                    required: true,
                    widthClassName: "col-xs-12 col-sm-6",
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module, formInitialize, getObjectSuccessAction}) => {
                            if (!formInitialize && !getObjectSuccessAction) {
                                const storeContactsField = module.viewMap.form.fields.find(f => f.path === 'storeContacts')
                                const hierarchicalContactsField = module.viewMap.form.fields.find(f => f.path === 'hierarchicalContacts')
                                storeContactsField.setValue([])
                                hierarchicalContactsField.setValue([])
                            }

                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-S-auditPreparation.AuditPreparation_storeContacts",
                                    store.getState,
                                    {data: {store: newValue || null}}
                                )
                            )
                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-S-auditPreparation.AuditPreparation_hierarchicalContacts",
                                    store.getState,
                                    {data: {store: newValue || null}}
                                )
                            )
                        }
                    }
                },
                {
                    path: "controlDate",
                    type: "datePicker",
                    tKey: "scheduledAuditDate",
                    startDate: moment().format("YYYY-MM-DD"),
                    widthClassName: "col-xs-12 col-sm-6"
                },
                {
                    path: "storeContacts",
                    display: "userName",
                    tKey: "storeContacts",
                    required: true,
                    widthClassName: "col-xs-12 col-sm-6",
                    filters: ["byStore"]
                },
                {
                    path: "hierarchicalContacts",
                    display: "userName",
                    tKey: "hierarchicalContacts",
                    required: true,
                    widthClassName: "col-xs-12 col-sm-6",
                    filters: ["byHierarchicalContact"]
                },
                {
                    path: "auditThemes",
                    display: "translatedFullName",
                    required: true,
                    filters: ["activeThemes", 'themesWithActiveControls']
                },
                {
                    path: "objectives",
                    type: "richTextEditor",
                    tKey: "objectives",
                    required: true
                },
                {
                    path: "auditors",
                    display: "userName",
                    tKey: "auditors",
                    required: true,
                    filters: ["byCentralAxis"]
                },
                {path: 'status', hidden: true},
                {path: 'buttons', hidden: true}
            ],
            onOpen: ({ store }) => {
                const state = store.getState()
                const statusId = _.get(state, 'edit.object.data.status.id')
                const isPastDraft = !!statusId && statusId !== 'draft'

                store.dispatch(setFieldVisibility('e_auditNumber', isPastDraft))
                store.dispatch(changeFieldDisabled('e_controlDate', isPastDraft))
                store.dispatch(changeFieldDisabled('e_store', isPastDraft))
                store.dispatch(changeFieldDisabled('e_auditThemes', isPastDraft))
            }
        },
    }
}